import {
  Button,
  type ButtonProps,
  Dialog as ChakraDialog,
  Portal,
} from "@chakra-ui/react";
import * as React from "react";
import { CloseButton } from "./close-button";

interface DialogContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
}

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>(function DialogContent(props, ref) {
  const {
    children,
    portalled = true,
    portalRef,
    backdrop = true,
    ...rest
  } = props;

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialog.Backdrop />}
      <ChakraDialog.Positioner>
        <ChakraDialog.Content
          ref={ref}
          alignItems="center"
          {...rest}
          asChild={false}
        >
          {children}
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  );
});

export const DialogCloseTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>(function DialogCloseTrigger(props, ref) {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="2"
      insetEnd="2"
      {...props}
      asChild
    >
      <CloseButton size="sm" ref={ref}>
        {props.children}
      </CloseButton>
    </ChakraDialog.CloseTrigger>
  );
});

export const DialogRoot = ChakraDialog.Root;
export const DialogFooter = ChakraDialog.Footer;
export const DialogHeader = ChakraDialog.Header;
export const DialogBody = ChakraDialog.Body;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogActionTrigger = ChakraDialog.ActionTrigger;

export interface DialogProps extends ChakraDialog.RootProps {
  label: string;
  children: React.ReactNode;
  title?: string;
  disabled?: boolean;
  buttonIcon?: React.ReactNode;
  buttonProps?: ButtonProps;
  contentProps?: ChakraDialog.ContentProps;
  [key: string]: any;
}

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
  function Dialog(props, ref) {
    const {
      children,
      title,
      label,
      buttonIcon,
      buttonProps,
      disabled,
      contentProps,
      ...rest
    } = props;

    return (
      <ChakraDialog.Root lazyMount>
        <ChakraDialog.Trigger disabled={disabled} asChild>
          <Button size="sm" {...buttonProps}>
            {buttonIcon}
            {label}
          </Button>
        </ChakraDialog.Trigger>
        <DialogContent {...contentProps}>
          {title && (
            <ChakraDialog.Header>
              <ChakraDialog.Title>{title}</ChakraDialog.Title>
            </ChakraDialog.Header>
          )}
          <ChakraDialog.Context>
            {(store) => (
              <ChakraDialog.Body p={10} {...rest}>
                {children}
              </ChakraDialog.Body>
            )}
          </ChakraDialog.Context>
          <DialogCloseTrigger />
        </DialogContent>
      </ChakraDialog.Root>
    );
  }
);
